// eligibility.js — the SINGLE source of truth for "does this operator serve this
// pad", called by BOTH the customer client (App.jsx opServesPad) and the server
// money-path (bookings-create servedBy). One implementation → a pad the customer
// can pick is always one the server accepts, so an eligibility split can never
// produce a price-mismatch 400 (Schritt-C G2 chokepoint).
//
// Read-time union (model b — operator.servedHelipads[] self-assign allowlist):
//
//   served = ( adminAssigned || (!adminAssignedOnly && selfAssigned) ) && !unserved
//
//     adminAssigned = location.operators[] includes the operator (admin-linked)
//     selfAssigned  = operator.servedHelipads[] includes the pad   (operator opt-in)
//     unserved      = operator.unservedHelipads[] includes the pad (hard opt-out)
//
// Pad class: when the LOCATION is `adminAssignedOnly`, the self-assign branch is
// IGNORED — only location.operators[] grants service (the operator cannot
// self-assign a restricted pad).
//
// REGRESSION (bit-identical to the pre-Schritt-C logic on today's data): with no
// servedHelipads and adminAssignedOnly=false (every existing pad), this reduces to
//   served = operators[].includes(opId) && !unserved
// — exactly what opServesPad / servedBy returned before.
//
// Pure: no I/O, no Sanity. servedSet/unservedSet are per-operator Sets of
// location._id (built once by the caller); padId is the location._id.
export function isOperatorServingPad({ operatorIds, adminAssignedOnly = false, opId, padId, servedSet = null, unservedSet = null }) {
  if (!opId || !padId) return false;
  // operators[] is null/undefined on pads that were never admin-linked (e.g. a
  // pad created after Schritt C and served purely via self-assign, like
  // location-ZZZZPKO). Treat as [] so the selfAssigned branch still applies —
  // the old `!Array.isArray(operatorIds) → false` guard made such pads
  // permanently unservable: the portal toggle WROTE servedHelipads correctly,
  // but every read-back (portal toggle state, funnel pad step, customer
  // lists/maps, bookings-create servedBy) computed served=false.
  const ids = Array.isArray(operatorIds) ? operatorIds : [];
  const adminAssigned = ids.includes(opId);
  const selfAssigned = !adminAssignedOnly && !!(servedSet && servedSet.has(padId));
  if (!(adminAssigned || selfAssigned)) return false;            // neither grants service
  if (unservedSet && unservedSet.has(padId)) return false;       // hard opt-out wins
  return true;
}

// Derive the operator-UI toggle state for ONE pad (3 classes):
//   'open'              → adminAssignedOnly=false → operator may toggle freely
//   'admin-assigned'    → restricted AND admin-linked → operator may toggle freely
//   'restricted-locked' → restricted AND NOT admin-linked → greyed, "assigned by admin"
// `served` is the current effective state (isOperatorServingPad) for the checkbox.
export function operatorPadToggleState({ operatorIds, adminAssignedOnly = false, opId }) {
  const adminAssigned = Array.isArray(operatorIds) && operatorIds.includes(opId);
  if (!adminAssignedOnly) return 'open';
  return adminAssigned ? 'admin-assigned' : 'restricted-locked';
}
